/**
 * One trap of a Bear Trap day: how big to run it, then the plan itself.
 *
 * The screen has a deliberate order. The SIZING comes first and is presented as
 * a recommendation with its reasoning attached — how many leads, how many
 * waves, how many rallies per wave — and the operator either takes it or types
 * their own numbers. Only then is a plan generated, and only for the roster
 * that was actually saved for this trap.
 *
 * Two things this must not do, both learned the hard way:
 *
 * - It must not prefill the override boxes with the recommendation. Prefilled
 *   numbers get sent on every request, which is how the planner's whole
 *   strategy_rules path became unreachable dead code once before, with the
 *   operator told "wave count 3 was supplied with the request" about a number
 *   no human had chosen. Empty means "use the recommendation", and the mode is
 *   an explicit choice.
 * - It must not hide `unknowns`. A recommended lead count derived from a trap
 *   nobody has entered yet is a different object from one counted off real
 *   rallies, and the operator has to be able to see which they are looking at.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { BearTrapNumber, BearTrapPlanRequest, BearTrapSizing } from '@kso/schemas';
import { useGenerateTrapPlan, useTrapSizing } from '../../api/queries';
import { Card, Notice, ReasonList, Value, WarningList } from '../../components/ui/primitives';
import { ErrorState, LoadingBlock } from '../../components/ui/states';
import { useT } from '../../i18n';
import { PersistedPlanView } from './PersistedPlanView';
import { PlanResult } from './PlanResult';

const MODE_IDS: Array<BearTrapPlanRequest['plannerMode']> = [
  'MAX_ALLIANCE_DAMAGE',
  'BALANCED_REWARDS',
  'PERSONAL_SCORE_ASSIST',
];

type Shape = 'RECOMMENDED' | 'MANUAL';

export function TrapPlanPanel({
  eventId,
  trapNumber,
  assignedCount,
  assignedMembers,
  memberById,
}: {
  eventId: string;
  trapNumber: BearTrapNumber;
  assignedCount: number;
  assignedMembers: Array<{ playerId: string; playerName: string | null }>;
  memberById: Map<string, string>;
}) {
  const t = useT();
  const sizing = useTrapSizing(eventId, trapNumber);
  const plan = useGenerateTrapPlan(eventId, trapNumber);

  const [shape, setShape] = useState<Shape>('RECOMMENDED');
  const [mode, setMode] = useState<BearTrapPlanRequest['plannerMode']>('MAX_ALLIANCE_DAMAGE');
  const [focusPlayerId, setFocusPlayerId] = useState('');
  // Blank, always. See the note at the top of this file.
  const [waveCount, setWaveCount] = useState('');
  const [ralliesPerWave, setRalliesPerWave] = useState('');

  const recommended = sizing.data?.sizing ?? null;
  const sessionId = sizing.data?.sessionId ?? null;

  function generate() {
    const overrides =
      shape === 'MANUAL'
        ? {
            ...(waveCount.trim() ? { waveCount: Number(waveCount) } : {}),
            ...(ralliesPerWave.trim() ? { ralliesPerWave: Number(ralliesPerWave) } : {}),
          }
        : /*
           * Accepting the recommendation SENDS it, rather than letting the
           * planner re-derive it. The operator saw these numbers and approved
           * them; the plan must be built from what they approved, not from
           * whatever the rules happen to say a minute later.
           */
          {
            ...(recommended?.recommendedWaveCount
              ? { waveCount: recommended.recommendedWaveCount }
              : {}),
            ...(recommended?.recommendedRalliesPerWave
              ? { ralliesPerWave: recommended.recommendedRalliesPerWave }
              : {}),
          };

    plan.mutate({
      plannerMode: mode,
      ...(mode === 'PERSONAL_SCORE_ASSIST' && focusPlayerId ? { focusPlayerId } : {}),
      ...overrides,
    });
  }

  const canGenerate =
    assignedCount > 0 && !(mode === 'PERSONAL_SCORE_ASSIST' && !focusPlayerId) && !plan.isPending;

  return (
    <Card
      title={t.bearDay.trapTitle(trapNumber, assignedCount)}
      actions={
        /*
         * The loop closes here. Entering this trap's rallies is what turns next
         * time's lead count from a figure quoted out of a rule into one counted
         * off real rows -- so the entry screen is one click from the plan, not
         * somewhere the operator has to go and find.
         */
        sessionId ? (
          <Link
            className="btn btn--sm btn--subtle"
            to={`/bear/history?session=${encodeURIComponent(sessionId)}`}
          >
            {t.bearDay.enterRallies}
          </Link>
        ) : null
      }
    >
      {assignedCount === 0 ? (
        <Notice severity="INFO">{t.bearDay.trapEmpty(trapNumber)}</Notice>
      ) : (
        <>
          {sizing.isPending ? (
            <LoadingBlock label={t.bearDay.loadingSizing} rows={2} />
          ) : sizing.error ? (
            <ErrorState error={sizing.error} />
          ) : recommended ? (
            <SizingSummary sizing={recommended} />
          ) : null}

          <div className="stack" style={{ gap: 10, marginTop: 14 }}>
            <div className="row" style={{ gap: 16, flexWrap: 'wrap' }}>
              <label className="row" style={{ gap: 6 }}>
                <input
                  type="radio"
                  name={`shape-${trapNumber}`}
                  checked={shape === 'RECOMMENDED'}
                  onChange={() => setShape('RECOMMENDED')}
                />
                <span>{t.bearDay.useRecommendation}</span>
              </label>
              <label className="row" style={{ gap: 6 }}>
                <input
                  type="radio"
                  name={`shape-${trapNumber}`}
                  checked={shape === 'MANUAL'}
                  onChange={() => setShape('MANUAL')}
                />
                <span>{t.bearDay.decideMyself}</span>
              </label>
            </div>

            {shape === 'MANUAL' ? (
              <div className="row" style={{ gap: 16, flexWrap: 'wrap' }}>
                <label className="row" style={{ gap: 6 }}>
                  <span className="muted small">{t.planner.waves}</span>
                  <input
                    type="number"
                    min={1}
                    max={10}
                    value={waveCount}
                    placeholder={t.planner.autoPlaceholder}
                    onChange={(event) => setWaveCount(event.target.value)}
                    style={{ width: 72 }}
                    aria-label={t.planner.waves}
                  />
                </label>
                <label className="row" style={{ gap: 6 }}>
                  <span className="muted small">{t.planner.ralliesPerWave}</span>
                  <input
                    type="number"
                    min={1}
                    max={20}
                    value={ralliesPerWave}
                    placeholder={t.planner.autoPlaceholder}
                    onChange={(event) => setRalliesPerWave(event.target.value)}
                    style={{ width: 72 }}
                    aria-label={t.planner.ralliesPerWave}
                  />
                </label>
                <span className="muted small">{t.bearDay.blankMeansAuto}</span>
              </div>
            ) : null}

            <div className="row" style={{ gap: 16, flexWrap: 'wrap' }}>
              <label className="row" style={{ gap: 6 }}>
                <span className="muted small">{t.planner.objective}</span>
                <select
                  value={mode}
                  onChange={(event) =>
                    setMode(event.target.value as BearTrapPlanRequest['plannerMode'])
                  }
                  aria-label={t.planner.objective}
                >
                  {MODE_IDS.map((id) => (
                    <option key={id} value={id}>
                      {t.planner.modes[id].label}
                    </option>
                  ))}
                </select>
              </label>

              {mode === 'PERSONAL_SCORE_ASSIST' ? (
                <label className="row" style={{ gap: 6 }}>
                  <span className="muted small">{t.planner.planAround}</span>
                  <select
                    value={focusPlayerId}
                    onChange={(event) => setFocusPlayerId(event.target.value)}
                    aria-label={t.planner.planAround}
                  >
                    <option value="">{t.bearDay.pickFocus}</option>
                    {assignedMembers.map((member) => (
                      <option key={member.playerId} value={member.playerId}>
                        {member.playerName ?? member.playerId}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
            </div>

            <p className="muted small" style={{ margin: 0 }}>
              {t.planner.modes[mode].blurb}
            </p>

            <div className="row">
              <button
                type="button"
                className="btn btn--primary"
                onClick={generate}
                disabled={!canGenerate}
              >
                {plan.isPending ? t.planner.generating : t.bearDay.generateForTrap(trapNumber)}
              </button>
              {mode === 'PERSONAL_SCORE_ASSIST' && !focusPlayerId ? (
                <span className="muted small">{t.bearDay.pickFocus}</span>
              ) : null}
            </div>
          </div>

          {plan.error ? (
            <div style={{ marginTop: 12 }}>
              <ErrorState error={plan.error} />
            </div>
          ) : null}

          {plan.data?.recommendationRunId ? (
            <div style={{ marginTop: 14 }}>
              <PersistedPlanView runId={plan.data.recommendationRunId} memberById={memberById} />
            </div>
          ) : plan.data ? (
            <div style={{ marginTop: 14 }}>
              <Notice severity="WARN">{t.planner.notPersisted}</Notice>
              <PlanResult plan={plan.data} memberById={memberById} />
            </div>
          ) : null}
        </>
      )}
    </Card>
  );
}

/**
 * The recommendation, with its reasoning and its gaps side by side.
 *
 * A null figure renders as "unknown" rather than as a dash that could be read
 * as zero — the distinction between "we measured none" and "we have not
 * measured" is the one thing this whole product is careful about.
 */
function SizingSummary({ sizing }: { sizing: BearTrapSizing }) {
  const t = useT();

  return (
    <div className="stack" style={{ gap: 10 }}>
      <div className="row" style={{ gap: 22, flexWrap: 'wrap' }}>
        <Figure label={t.bearDay.recommendedLeads} value={sizing.recommendedLeadCount} />
        <Figure label={t.planner.waves} value={sizing.recommendedWaveCount} />
        <Figure label={t.planner.ralliesPerWave} value={sizing.recommendedRalliesPerWave} />
        <Figure label={t.bearDay.totalLaunches} value={sizing.totalLaunches} />
      </div>

      {sizing.observedLeadCount !== null ? (
        <p className="muted small" style={{ margin: 0 }}>
          {sizing.observedLeadSource === 'RECORDED_RALLIES'
            ? t.bearDay.observedCounted(sizing.observedLeadCount, sizing.observedLeadDetail ?? '')
            : t.bearDay.observedQuoted(sizing.observedLeadCount)}
        </p>
      ) : null}

      {sizing.basis.length > 0 ? <ReasonList reasons={sizing.basis} /> : null}
      <WarningList warnings={sizing.unknowns} severity="INFO" />

      <p className="muted small" style={{ margin: 0 }}>
        {t.bearDay.sizingConfidence(Math.round(sizing.confidence * 100))}
      </p>
    </div>
  );
}

function Figure({ label, value }: { label: string; value: number | null }) {
  const t = useT();
  return (
    <div>
      <div className="muted small">{label}</div>
      <div style={{ fontSize: 22, fontWeight: 600 }}>
        <Value absent={t.common.unknown}>{value}</Value>
      </div>
    </div>
  );
}
